import { Injectable } from '@angular/core';
import { Constants } from '../helper/constants';
import { HttpService } from '../helper/httpService';
import { Observable, of, throwError } from 'rxjs';
import { QuestionAndAnswerModel } from '../viewmodels/questionAndAnswer.model';
import { QuestionProcessModel } from '../viewmodels/questionProcess.model';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { StorageService } from './storage.service';

@Injectable()
export class QuestionService {
  private questionServiceUrl = Constants.serverUrl + 'api/question/';
  private activeRequests = new Map<string, Observable<any>>(); // Store active questions requests by input

  constructor(
    public http: HttpService,
    private storageService: StorageService
  ) {}

  /** setLocalQuestionsAndAnswers */
  setLocalQuestionsAndAnswers(questionsAnswersList: Array<QuestionAndAnswerModel>): void {
    this.storageService.setQuestionsAndAnswers(questionsAnswersList);
  }

  /** getLocalQuestionsAndAnswers */
  getLocalQuestionsAndAnswers(): Array<QuestionAndAnswerModel> {
    return this.storageService.getQuestionsAndAnswers();
  }

  /** getQuestions (this method uses a replay subject to manage multiple requests) */
  getQuestions(pcnNumber: string, questionProcess: QuestionProcessModel): Observable<any> {
    const requestKey = this.getRequestKey(pcnNumber, questionProcess); // Generate unique key

    if (this.activeRequests.has(requestKey)) {
      // console.log('[Service] Returning cached request for key:', requestKey);
      const cachedRequest = this.activeRequests.get(requestKey);
      if (cachedRequest) {
        return cachedRequest; // ✅ Reuse active request
      }
    }

    // console.log('[Service] Sending request with data:', { pcnNumber, questionProcess });

    const request$ = this.http
      .Post(`${this.questionServiceUrl}getQuestions/${pcnNumber}`, JSON.stringify(questionProcess))
      .pipe(
        tap((questions) => {
          // console.log('[Service] GetQuestions request success:', questions);
        }),
        catchError((error) => {
          console.error('[Service] GetQuestions request failed:', error);
          this.activeRequests.delete(requestKey); // Remove failed requests
          return throwError(error);
        }),
        finalize(() => {
          // console.log('[Service] GetQuestions request complete for:', requestKey);
          this.activeRequests.delete(requestKey); // ✅ Remove after completion
        }),
        shareReplay(1) // ✅ Share the result with all subscribers
      );

    this.activeRequests.set(requestKey, request$); // Store the active request
    return request$;
  }

  /** Forces a manual clear of the cached questions **/
  clearCachedQuestions() {
    this.activeRequests.clear();
  }

  /** getDecisionActions */
  getDecisionActions(): Observable<any> {
    return this.http.Get(this.questionServiceUrl + 'getDecisionActions');
  }

  /** setLocalDecisionActions */
  setLocalDecisionActions(value) {
    localStorage.setItem('rm_decision_actions', JSON.stringify(value));
  }

  /** getLocalDecisionActions */
  getLocalDecisionActions() {
    return localStorage.getItem('rm_decision_actions') !== null
      ? JSON.parse(localStorage.getItem('rm_decision_actions'))
      : [];
  }

  /** setLocalQuestionTypes */
  setLocalQuestionTypes(value) {
    localStorage.setItem('rm_question_types', JSON.stringify(value));
  }

  /** getLocalQuestionTypes */
  getLocalQuestionTypes() {
    if (localStorage.getItem('rm_question_types') !== null) {
      return JSON.parse(localStorage.getItem('rm_question_types'));
    }
    return [];
  }

  /** getQuestionsTypes */
  getQuestionTypes(): Observable<any> {
    return this.http.Get(this.questionServiceUrl + 'getQuestionTypes');
  }

  /** setLocalQuestionClasses */
  setLocalQuestionClasses(value) {
    localStorage.setItem('rm_question_classes', JSON.stringify(value));
  }

  /** getLocalQuestionClasses */
  getLocalQuestionClasses() {
    if (localStorage.getItem('rm_question_classes') !== null) {
      return JSON.parse(localStorage.getItem('rm_question_classes'));
    }
    return [];
  }

  /** getQuestionsClasses */
  getQuestionClasses(): Observable<any> {
    return this.http.Get(this.questionServiceUrl + 'getQuestionClasses');
  }

  /** setLocalPoliciesAnswers */
  setLocalPoliciesAnswers(value) {
    localStorage.setItem('rm_policies_answers', JSON.stringify(value));
  }

  /** getLocalPoliciesAnswers */
  getLocalPoliciesAnswers() {
    if (localStorage.getItem('rm_policies_answers') !== null) {
      return JSON.parse(localStorage.getItem('rm_policies_answers'));
    }
    return [];
  }

  /** Generate a unique key based on PCN + request params */
  private getRequestKey(pcnNumber: string, questionProcess: QuestionProcessModel): string {
    return `${pcnNumber}_${JSON.stringify(questionProcess)}`;
  }
}
